import type {
  SearchKeywordPredicate,
  SearchRangeBound,
  SearchResponseFilterOutput,
} from '@iconik-io/iconik-api/src/services/search';

const METADATA_PREFIX = 'metadata.';

/** Names of the fields a natural search filters on that the panel has no control for. */
const KNOWN_FIELD_LABELS: Record<string, string> = {
  ancestor_collections: 'Collection',
  in_collections: 'In collection',
  created_by_user: 'Created by',
  date_created: 'Date created',
  date_modified: 'Date modified',
  file_names: 'File name',
  id: 'ID',
  media_type: 'Media type',
};

export type NaturalFilterLabelResolvers = {
  /** Names the fields the app knows, such as metadata fields. */
  getFieldLabel?: (field: string) => string | undefined;
};

const humanizeFieldName = (field: string): string => {
  const name = field.startsWith(METADATA_PREFIX)
    ? field.slice(METADATA_PREFIX.length)
    : field;
  const words = name.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const isRangeBound = (value: unknown): value is SearchRangeBound =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  ('gt' in value || 'gte' in value || 'lt' in value || 'lte' in value);

const getFieldName = (
  entry: SearchResponseFilterOutput,
  { getFieldLabel }: NaturalFilterLabelResolvers,
): string =>
  entry.label ||
  getFieldLabel?.(entry.field) ||
  KNOWN_FIELD_LABELS[entry.field] ||
  humanizeFieldName(entry.field);

// The search names the opaque ids it filters on (users, storages,
// collections), so a value it did not name is shown as it is.
const getValueNames = (
  entry: SearchResponseFilterOutput,
  predicate: SearchKeywordPredicate,
): string[] =>
  (Array.isArray(predicate.value) ? predicate.value : [predicate.value])
    .flat()
    .map((value) => {
      if (isRangeBound(value)) {
        return describeRange(value);
      }
      const text = String(value);
      return entry.value_labels?.[text] || text;
    });

/** Whether a filter of a natural-search answer is on the query's words. */
export const isNaturalWordFilter = ({
  predicate,
}: SearchResponseFilterOutput): boolean =>
  !('op' in predicate) && !('text' in predicate);

/** The names of the values a keyword filter of a natural-search answer holds. */
export const describeNaturalFilterValues = (
  entry: SearchResponseFilterOutput,
): string[] =>
  'op' in entry.predicate ? getValueNames(entry, entry.predicate) : [];

const describeRange = ({ gt, gte, lt, lte }: SearchRangeBound): string => {
  const from = gte ?? gt;
  const to = lte ?? lt;
  if (from !== null && from !== undefined && to !== null && to !== undefined) {
    return `${from} to ${to}`;
  }
  if (from !== null && from !== undefined) {
    return `from ${from}`;
  }
  return `until ${to}`;
};

/**
 * A filter of a natural-search answer as a short sentence, for the filters the
 * panel does not show, such as those given up on or ignored.
 */
export function describeNaturalFilter(
  entry: SearchResponseFilterOutput,
  resolvers: NaturalFilterLabelResolvers = {},
): string {
  const { predicate } = entry;
  // Only keyword predicates carry an `op`: the others are the query's words.
  if (!('op' in predicate)) {
    if ('text' in predicate) {
      return `about “${predicate.text}”`;
    }
    const words = predicate.terms
      ? [predicate.terms]
      : (predicate.any_of ?? []);
    return words.map((word) => `“${word}”`).join(' or ');
  }

  const fieldLabel = getFieldName(entry, resolvers);
  if (predicate.op === 'exists') {
    return `${fieldLabel} ${predicate.negate ? 'is not set' : 'is set'}`;
  }
  const joined = getValueNames(entry, predicate).join(
    predicate.op === 'all' ? ' and ' : ' or ',
  );
  return `${fieldLabel}: ${predicate.negate ? 'not ' : ''}${joined}`;
}

export type NaturalFilterChipLabels = {
  fieldLabel: string;
  modifierLabel?: string;
  valueLabel: string;
};

const getModifierLabel = (
  entry: SearchResponseFilterOutput,
  predicate: SearchKeywordPredicate,
  valueCount: number,
): string => {
  const { negate } = predicate;
  if (entry.field === 'ancestor_collections') {
    return negate ? 'not within' : 'within';
  }
  if (entry.field === 'in_collections') {
    return negate ? 'not in' : 'in';
  }
  if (valueCount > 1 && predicate.op === 'all') {
    return negate ? 'exclude all of' : 'include all of';
  }
  if (valueCount > 1) {
    return negate ? 'exclude any of' : 'include any of';
  }
  return negate ? 'is not' : 'is';
};

/**
 * A keyword filter of a natural-search answer as a filter chip reads it:
 * `field · modifier · value`, in the words the panel's chips use.
 */
export function describeNaturalFilterChip(
  entry: SearchResponseFilterOutput,
  resolvers: NaturalFilterLabelResolvers = {},
): NaturalFilterChipLabels {
  const fieldLabel = getFieldName(entry, resolvers);
  const { predicate } = entry;
  if (!('op' in predicate)) {
    return { fieldLabel, valueLabel: describeNaturalFilter(entry, resolvers) };
  }
  if (predicate.op === 'exists') {
    return {
      fieldLabel,
      modifierLabel: predicate.negate ? 'is not' : 'is',
      valueLabel: 'set',
    };
  }
  const values = getValueNames(entry, predicate);
  return {
    fieldLabel,
    modifierLabel: getModifierLabel(entry, predicate, values.length),
    valueLabel: values.join(', '),
  };
}
